// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-19
// protected: NO
// =============================================================================
// Penerimaan Gudang: daftar barang produksi menunggu + scan-to-receive (MASUK_GUDANG).
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, PackageCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";
import { QrBatchInput } from "@/v2/components/QrBatchInput";
import { defaultGudang, isOwner, WAREHOUSES, resultToast } from "@/v2/components/helpers";

export default function GudangPage() {
  const { me, api } = useV2Auth();
  const [qrs, setQrs] = useState([]);
  const [pending, setPending] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);  // {done,total} — indikator per potongan (T-A-9)
  const [loc, setLoc] = useState(defaultGudang(me?.region));
  const [needsTransfer, setNeedsTransfer] = useState([]);
  const REGION_LABEL = { SBY: "Surabaya", SMD: "Samarinda" };

  const loadPending = useCallback(async () => {
    // S-E: /api/v2/items kini objek paginasi; `limit` = alias page_size (kompat). Baca .items.
    const r = await api("/api/v2/items?state=PRODUCED&limit=100");
    setPending(r.ok ? (await r.json()).items || [] : []);
  }, [api]);
  const loadNeedsTransfer = useCallback(async () => {
    const r = await api("/api/v2/items?needs_transfer=true&limit=200");
    setNeedsTransfer(r.ok ? (await r.json()).items || [] : []);
  }, [api]);
  useEffect(() => { loadPending(); loadNeedsTransfer(); }, [loadPending, loadNeedsTransfer]);
  useV2LiveRefresh(() => { loadPending(); loadNeedsTransfer(); });  // FASE 7B

  const CHUNK = 200;  // SLICE T-A (T-A-9): kirim per potongan, bukan 1 permintaan raksasa.
  const receive = async () => {
    if (qrs.length === 0) { toast.error("Scan minimal 1 QR"); return; }
    setBusy(true); setResult(null); setProgress({ done: 0, total: qrs.length });
    const agg = { received: 0, failed_count: 0, success: [], failed: [] };
    try {
      for (let i = 0; i < qrs.length; i += CHUNK) {
        const part = qrs.slice(i, i + CHUNK);
        const r = await api("/api/v2/warehouse/receive", { method: "POST",
          body: JSON.stringify({ qr_ids: part, location: isOwner(me?.role) ? loc : undefined }) });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { toast.error(data.detail || "Gagal memproses sebagian batch"); break; }
        agg.received += data.received || 0;
        agg.failed_count += data.failed_count || 0;
        if (data.success) agg.success.push(...data.success);
        if (data.failed) agg.failed.push(...data.failed);
        setProgress({ done: Math.min(i + CHUNK, qrs.length), total: qrs.length });
      }
      // Ringkasan akhir tunggal: berapa berhasil, berapa gagal, + daftar gagal beserta alasan.
      setResult(agg); resultToast(toast, agg, "diterima"); setQrs([]);
      loadPending(); loadNeedsTransfer();
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <div className="space-y-5" data-testid="v2-gudang-page">
      <h2 className="text-2xl font-semibold">Penerimaan Gudang</h2>
      <div className="grid lg:grid-cols-2 gap-4">
        {isOwner(me?.role) ? (
          <Card className="p-5 rounded-2xl grid place-items-center text-center text-sm text-muted-foreground min-h-[160px]" data-testid="v2-gudang-readonly">
            Mode Owner: tampilan read-only (audit/monitoring). Aksi terima/scan gudang dinonaktifkan.
          </Card>
        ) : (
        <Card className="p-5 rounded-2xl">
          <div className="flex items-center gap-2 mb-3"><PackageCheck className="w-4 h-4 text-primary" /><h3 className="font-semibold">Scan terima (PRODUCED → IN_WAREHOUSE)</h3></div>
          <QrBatchInput value={qrs} onChange={setQrs} testid="v2-gudang-qr" />
          <Button className="mt-4" onClick={receive} disabled={busy} data-testid="v2-gudang-receive">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}Terima Merchandize
          </Button>
          {busy && progress && (
            <div className="mt-3 text-sm text-muted-foreground" data-testid="v2-gudang-progress">
              Memproses… {progress.done} dari {progress.total} diproses
            </div>
          )}
          {result && (
            <div className="mt-4 space-y-1 text-sm">
              {result.success?.map((s) => <div key={s.qr_id} className="flex items-center gap-2 text-[hsl(var(--badge-success))]"><CheckCircle2 className="w-4 h-4" />{s.qr_id} diterima</div>)}
              {result.failed?.map((f) => <div key={f.qr_id} className="flex items-center gap-2 text-destructive"><XCircle className="w-4 h-4" />{f.qr_id}: {f.reason}</div>)}
            </div>
          )}
        </Card>
        )}
        <Card className="p-5 rounded-2xl">
          <h3 className="font-semibold mb-3">Menunggu diterima ({pending.length})</h3>
          <div className="max-h-80 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>QR</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead></TableRow></TableHeader>
              <TableBody>
                {pending.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4 text-sm">Tidak ada.</TableCell></TableRow>}
                {pending.map((it) => (
                  <TableRow key={it.qr_id} data-testid="v2-pending-row">
                    <TableCell className="font-mono text-xs">{it.qr_id}</TableCell>
                    <TableCell>{it.club}</TableCell><TableCell>{it.size}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl" data-testid="v2-gudang-needtransfer">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold">Perlu Transfer Gudang ({needsTransfer.length})</h3>
          <Badge variant="warning">Lintas region</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Merchandize milik region lain yang berada di gudang ini wajib ditransfer ke Gudang pemilik
          sebelum bisa dikirim ke store. Gunakan menu <b>Transfer Gudang</b>.
        </p>
        <div className="hidden md:block max-h-80 overflow-y-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>QR</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
              <TableHead>Lokasi</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {needsTransfer.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4 text-sm">Tidak ada item yang perlu transfer.</TableCell></TableRow>}
              {needsTransfer.map((it) => (
                <TableRow key={it.qr_id} data-testid="v2-needtransfer-row">
                  <TableCell className="font-mono text-xs">{it.qr_id}</TableCell>
                  <TableCell>{it.club}</TableCell>
                  <TableCell>{it.size === "-" ? "—" : it.size}</TableCell>
                  <TableCell className="text-xs">{REGION_LABEL[it.current_region] || it.current_location}</TableCell>
                  <TableCell>
                    <Badge variant="warning">
                      Perlu Transfer ke Gudang {REGION_LABEL[it.transfer_required_region] || it.transfer_required_region}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Mobile: card list */}
        <div className="md:hidden max-h-96 overflow-y-auto space-y-3" data-testid="v2-needtransfer-cards">
          {needsTransfer.length === 0 && (
            <div className="text-center text-muted-foreground py-4 text-sm rounded-xl border border-dashed">Tidak ada item yang perlu transfer.</div>
          )}
          {needsTransfer.map((it) => (
            <div key={it.qr_id} className="rounded-xl border p-3 space-y-1.5" data-testid="v2-needtransfer-row-m">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs break-all">{it.qr_id}</span>
                <Badge variant="outline" className="shrink-0">{it.size === "-" ? "—" : it.size}</Badge>
              </div>
              <div className="text-sm">{it.club}</div>
              <div className="text-xs text-muted-foreground">Lokasi: {REGION_LABEL[it.current_region] || it.current_location}</div>
              <Badge variant="warning">
                Perlu Transfer ke Gudang {REGION_LABEL[it.transfer_required_region] || it.transfer_required_region}
              </Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
